import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Play,
  Volume2,
  Heart,
  Clock,
  ArrowLeft,
  Lightbulb,
  Pause,
  RotateCcw,
  Maximize,
  Mic,
  Zap,
  Feather,
  Star,
  Sun,
  Music,
  Loader2
} from "lucide-react";

// --- Asset Imports ---
import grumpycloud from "../Photos/GrumpyCloud.png";
import Safari from "../Photos/Safari.png";
import SilentTree from "../Photos/SilentTree.png";
import Musical from "../Photos/Musical.png";
import Croco from "../Photos/Crocodile.png";
import Zippy from "../Photos/Zippy.png";
import TheR from "../Photos/TheR.png";
import Frog from "../Photos/Frog.png";
import ABC from "../Photos/ABC DANCE.png";

// --- Story Data ---
const ASSET_BASE_URL = "https://github.com/OmkarMore278/TalkyToon_Videos/releases/download/v1";

const ALL_STORIES = [
  {
    id: 101,
    title: "The Grumpy Cloud's Sunny Day 🌤️",
    desc: "A cloud learns to express frustration and find happiness. Focuses on vocal emotional tone.",
    category: "Emotional Regulation",
    icon: Heart,
    duration: "1 min",
    imageUrl: grumpycloud,
    videoUrl: `${ASSET_BASE_URL}/Story_GrumpyCloud.mp4`,
    fullText: "Once upon a time, there was a tiny cloud who was always grumpy. Every day, he watched the sun shine happily. One morning, the sun noticed him and decided to shine a little brighter. The warm sunlight gave the cloud a gentle sunny hug. And something magical happened. He felt a tiny bit happier. He smiled for the very first time. From that day on, the little cloud wasn't so grumpy anymore.",
    speechGoal: "Practice varying vocal tone to match emotions.",
  },
  {
    id: 102,
    title: "The Great 'S' Sound Safari 🦓",
    desc: "Join Sassy the Snake on a safari adventure full of silly 'S' sounds!",
    category: "Phonics Focus",
    icon: Mic,
    duration: "1 min",
    imageUrl: Safari,
    videoUrl: `${ASSET_BASE_URL}/Story_S_Safari.mp4`,
    fullText: "Welcome to the Great SS Sound Safari. Today, we're joining Sammy the snake, who loves words that start with the letter S. First stop, sun. S-s-sun. Next, sparkling stars. S stars! Look! Silly socks! S-S socks! And here's a slow snail! S-snail! Surprise! A shiny letter S, just for you! Thanks for joining our S Sound Safari! Keep practicing your S sounds!",
    speechGoal: "Improve accuracy and clarity of the 'S' sound.",
  },
  {
    id: 103,
    title: "The Talking Tree's Secret Whispers 🌳",
    desc: "A magical tree that only speaks when you whisper softly and clearly.",
    category: "Volume Control",
    icon: Heart,
    duration: "1 min",
    imageUrl: SilentTree,
    videoUrl: `${ASSET_BASE_URL}/TheSilentTree.mp4`,
    fullText: "One day I wandered into a quiet forest where the trees swayed as if they had something to say. That's when I found a giant old tree and suddenly it whispered my name. Hello there, the tree whispered. I've been waiting to share a little secret with you. Then it told me its secret. Every leaf in this forest carries a tiny bit of magic. If you listen closely, the tree whispered, you can hear them sharing stories of kindness. The tree gave me one magical leaf, a gift to remind me to always be kind. As I walked away, the tree whispered, Mmm, come back anytime. I'll always have peppers. And that's the story of the magical talking tree and its secret whispers.",
    speechGoal: "Learn to control vocal volume and speak softly with clarity.",
  },
  {
    id: 104,
    title: "Zippy the Zoom Racer's Fast Talk 🏎️",
    desc: "A fast-paced race where kids match the announcer's speed and excitement.",
    category: "High Energy",
    icon: Zap,
    duration: "1 min",
    imageUrl: Zippy,
    videoUrl: `${ASSET_BASE_URL}/ZippyCar.mp4`,
    fullText: "The engines roared. The crowd cheered. Zippy the Zoom Racer zoomed to the starting line! The race announcer shouted super fast, “Ready… set… GO!” Zippy sped across the track, swishing past every corner. Kids joined the excitement by matching the announcer’s fast, fun style. The faster they spoke, the faster Zippy raced! With a final whoosh, Zippy crossed the finish line—victory!",
    speechGoal: "Practice fast-paced articulation and breath control.",
  },
  {
    id: 105,
    title: "Freddie the Frog's Feelings 🐸",
    desc: "Freddie feels different emotions—kids learn to name and express feelings.",
    category: "Emotional Regulation",
    icon: Feather,
    duration: "1 min",
    imageUrl: Frog,
    videoUrl: `${ASSET_BASE_URL}/The.Frog.mp4`,
    fullText: "Freddie the Frog sat on his favorite lily pad. One moment he felt happy, the next he felt sad, then excited! Every time Freddie felt something new, he croaked, “Ribbit! I feel…” Kids helped Freddie by saying feelings out loud with him. Together, they learned that every feeling is okay, and naming emotions makes them easier to understand.",
    speechGoal: "Build vocabulary for emotional expression.",
  },
  {
    id: 106,
    title: "The Magical Mystery of 'R' 🌈",
    desc: "Rainbows, robots, rhinos—an adventure full of tricky 'R' sounds.",
    category: "Phonics Focus",
    icon: Star,
    duration: "1 min",
    imageUrl: TheR,
    videoUrl: `${ASSET_BASE_URL}/MagicalR.mp4`,
    fullText: "Rosie the Rhino raced under a rainbow, roaring with excitement. She reached a room full of red robots making rattling noises. Every clue in the mystery started with the letter 'R'. To solve it, kids practiced strong, clear 'R' sounds—‘rrrrr!’—just like Rosie. With each clue spoken correctly, the rainbow glowed brighter.",
    speechGoal: "Strengthen pronunciation of the 'R' sound.",
  },
  {
    id: 107,
    title: "Counting Crocodiles' Dinner 🐊",
    desc: "A fun early-math challenge that mixes counting and clear speech.",
    category: "Early Math & Logic",
    icon: Sun,
    duration: "1 min",
    imageUrl: Croco,
    videoUrl: `${ASSET_BASE_URL}/CrocNumber.mp4`,
    fullText: "Seven hungry crocodiles gathered at the riverbank. They needed help counting their dinner! 'One fish, two fish…' the children counted clearly. The crocs nodded happily with each number. The clearer the counting, the faster the crocs ate. By the end, every crocodile was full—and children were expert counters!",
    speechGoal: "Improve number articulation and sequencing.",
  },
  {
    id: 108,
    title: "Melody the Musical Martian 👽",
    desc: "An alien who only understands singing—kids sing back simple melodies.",
    category: "Rhythm & Music",
    icon: BookOpen,
    duration: "1 min",
    imageUrl: Musical,
    videoUrl: `${ASSET_BASE_URL}/MusicalMelody.mp4`,
    fullText: "Melody the Martian floated down from the stars, humming a tune. But she didn’t understand normal speech—only singing! Kids sang simple notes back to her. ‘Laaa… loo… leee…’ With each melody matched, Melody's spaceship lit up in sparkly colors. Together, they created a musical path back home.",
    speechGoal: "Develop rhythm, melody matching, and vocal control.",
  },
  {
    id: 109,
    title: "ABC Dance Song 💃",
    desc: "Get moving while learning! Kids dance along to the alphabet.",
    category: "Rhythm & Music",
    icon: Music, 
    duration: "1 min",
    imageUrl: ABC,
    videoUrl: `${ASSET_BASE_URL}/ABCFriends.mp4`,
    fullText: "Jump, wiggle, and shake! The ABC Dance Song turns the alphabet into a full-body workout. As each letter appears, kids use their arms and legs to strike a pose—making a wide 'A' or a curvy 'S'.",
    speechGoal: "Improve letter sound recognition and gross motor coordination.",
  },
];

const StoryNotFound = ({ navigate }) => (
  <div className="text-center py-20 bg-white/90 rounded-2xl shadow-xl border border-red-300 max-w-lg mx-auto m-4">
    <h2 className="text-3xl font-bold text-red-600 mb-4">Story Not Found 😥</h2>
    <p className="text-gray-600 mb-6">Oops! We couldn't find the requested story.</p>
    <button
      onClick={() => navigate("/")}
      className="flex items-center gap-2 mx-auto px-6 py-3 bg-indigo-500 hover:bg-indigo-600 rounded-lg font-semibold text-white transition-all"
    >
      <ArrowLeft className="w-5 h-5" /> Go Back
    </button>
  </div>
);

const DetailItem = ({ icon: Icon, title, value }) => (
  <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-100 rounded-xl flex-1">
    <div className="p-2 bg-white rounded-lg text-indigo-500 shadow-sm">
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight">{title}</span>
      <p className="text-sm font-bold text-slate-700 leading-tight">{value}</p>
    </div>
  </div>
);

export default function StoryDetailPage() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isBuffering, setIsBuffering] = useState(true);

  const story = useMemo(() => ALL_STORIES.find((s) => s.id === parseInt(storyId)), [storyId]);
  const isLocalVideo = story && story.videoUrl && !story.youtubeId;

  useEffect(() => {
    if (!isLocalVideo || !videoRef.current) return;
    const video = videoRef.current;
    
    const updateProgress = () => video.duration && setProgress((video.currentTime / video.duration) * 100);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => setIsBuffering(true);
    const handleCanPlay = () => setIsBuffering(false);
    const handleEnded = () => { setIsPlaying(false); setProgress(100); };

    video.addEventListener("timeupdate", updateProgress);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", updateProgress);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("ended", handleEnded);
    };
  }, [story, isLocalVideo]);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else {
      if (progress >= 100) videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => console.error("Playback error:", err));
    }
  };

  if (!story) return <StoryNotFound navigate={navigate} />;

  const IconComponent = progress >= 100 ? RotateCcw : isPlaying ? Pause : Play;
  const buttonText = progress >= 100 ? "Replay" : isPlaying ? "Pause" : "Play Story";

  return (
    <div className="min-h-screen bg-[#FAFBFC] text-slate-800">
      {/* Slim Top Bar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-500 font-semibold hover:text-indigo-600 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="hidden sm:block text-center">
            <p className="text-xs font-bold text-slate-700">{story.title}</p>
          </div>
          <div className="w-10" /> {/* Spacer */}
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* --- LEFT: Video & Script --- */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Player Container - Slimmer Ring */}
            <div className="relative group aspect-video bg-slate-900 rounded-2xl overflow-hidden shadow-lg border border-slate-200">
              {story.youtubeId ? (
                <iframe
                  title={story.title}
                  src={`https://www.youtube-nocookie.com/embed/${story.youtubeId}?rel=0&modestbranding=1`}
                  className="w-full h-full"
                  allowFullScreen
                />
              ) : (
                <>
                  <video
                    ref={videoRef}
                    src={story.videoUrl}
                    poster={story.imageUrl}
                    className="w-full h-full object-contain cursor-pointer"
                    onClick={handlePlayPause}
                    playsInline
                    // crossOrigin="anonymous"
                    preload="auto"
                  />
                  
                  <AnimatePresence>
                    {isBuffering && (
                      <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="absolute inset-0 flex items-center justify-center bg-black/10 backdrop-blur-[2px]"
                      >
                        <Loader2 className="w-10 h-10 text-white animate-spin" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}

              {/* Progress Bar */}
              {isLocalVideo && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                  <motion.div 
                    className="h-full bg-indigo-500" 
                    style={{ width: `${progress}%` }} 
                  />
                </div>
              )}
            </div>

            {/* Compact Controls */}
            {isLocalVideo && (
              <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
                <button
                  onClick={handlePlayPause}
                  className={`flex-1 h-12 rounded-xl font-bold text-white flex items-center justify-center gap-2 text-sm transition-all active:scale-95 ${
                    isPlaying ? "bg-slate-700" : "bg-indigo-600 shadow-md shadow-indigo-100"
                  }`}
                >
                  <IconComponent className="w-4 h-4" /> {buttonText}
                </button>

                <div className="flex gap-2">
                  <button onClick={() => (videoRef.current.currentTime -= 10)} className="w-10 h-12 flex items-center justify-center bg-slate-50 text-slate-500 rounded-xl hover:bg-slate-100">
                    <RotateCcw className="w-4 h-4 -scale-x-100" />
                  </button>
                  <button onClick={() => videoRef.current.requestFullscreen()} className="w-10 h-12 flex items-center justify-center bg-slate-50 text-slate-500 rounded-xl hover:bg-slate-100">
                    <Maximize className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Story Transcript - Smaller Text */}
            <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-indigo-500" />
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-tight">Transcript</h2>
              </div>
              <p className="text-base md:text-lg text-slate-600 font-serif leading-relaxed italic">
                "{story.fullText}"
              </p>
            </section>
          </div>

          {/* --- RIGHT: Sidebar Info --- */}
          <aside className="lg:col-span-5 space-y-4">
            
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{story.title}</h1>
              <p className="text-sm text-slate-500 leading-relaxed mb-6">{story.desc}</p>

              <div className="flex gap-3">
                <DetailItem icon={Clock} title="Time" value={story.duration} />
                <DetailItem icon={Heart} title="Focus" value={story.category.split(' ')[0]} />
              </div>
            </div>

            {/* Compact Practice Tip */}
            <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-2xl p-6 text-white shadow-lg shadow-indigo-100">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-4 h-4 text-indigo-200" />
                <h3 className="text-xs font-bold uppercase tracking-widest text-indigo-100">Practice Goal</h3>
              </div>
              <p className="text-base font-medium leading-snug">
                {story.speechGoal}
              </p>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3">
                <Volume2 className="w-5 h-5 opacity-60" />
                <span className="text-[11px] font-semibold opacity-80">Focus on clear articulation</span>
              </div>
            </div>

          </aside>

        </div>
      </main>
    </div>
  );
}